import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { AppHttpClientApi } from '../../helpers/api/http-client-api';
import { HttpErrorHandlerService } from '@core/error-handling/http-error-handler.service';
import {
  ExamTypeInfo,
  KinshipType,
  ManualEntryWithFamilyPayload,
  ManualEntryWithFamilyResponse
} from './exam-type.interface';
import { FamilyExamType } from '@model/family-member/create-family-members.model';

interface ExamTypeBackendResponse {
  examId?: string;
  examName?: string;
  examGroupName?: string;
  familyType?: FamilyExamType;
  requiresFamilyMembers?: boolean;
  requiredRelatives?: number;
}

@Injectable({
  providedIn: 'root'
})
export class ExamTypeService {
  private readonly httpClient = inject(AppHttpClientApi);
  private readonly errorHandler = inject(HttpErrorHandlerService);
  private readonly baseUrl = 'orders';

  private readonly examTypeCache = new Map<string, ExamTypeInfo>();

  getExamTypeInfo(examId: string): Observable<ExamTypeInfo> {
    if (!examId) {
      return of(this.getDefaultExamTypeInfo());
    }

    const cached = this.examTypeCache.get(examId);
    if (cached) {
      return of(cached);
    }

    const url = `${this.baseUrl}/exam-type/${examId}`;

    return this.httpClient.getRaw<ExamTypeBackendResponse>(url).pipe(
      map((response) => this.mapBackendResponse(response)),
      tap((info) => {
        this.examTypeCache.set(examId, info);
      }),
      catchError(() => {
        return of(this.getDefaultExamTypeInfo());
      })
    );
  }

  getRequiredKinshipTypes(
    examTypeInfo: ExamTypeInfo,
    patientSex?: string
  ): KinshipType[] {
    if (!examTypeInfo.requiresFamilyMembers) {
      return [];
    }

    if (examTypeInfo.isTrio) {
      return [KinshipType.PADRE, KinshipType.MADRE];
    }

    if (examTypeInfo.isDuo) {
      return [KinshipType.PADRE, KinshipType.MADRE];
    }

    if (examTypeInfo.isFuturosPadres) {
      if (patientSex === 'M') {
        return [KinshipType.FUTURA_MADRE];
      } else if (patientSex === 'F') {
        return [KinshipType.FUTURO_PADRE];
      }
      return [KinshipType.FUTURA_MADRE, KinshipType.FUTURO_PADRE];
    }

    return [];
  }

  validateFamilyMembers(
    examTypeInfo: ExamTypeInfo,
    familyMembers: { tipoParentesco: KinshipType }[]
  ): { valid: boolean; errorMessage?: string } {
    if (!examTypeInfo.requiresFamilyMembers) {
      return { valid: true };
    }

    const types = familyMembers.map((fm) => fm.tipoParentesco);

    if (examTypeInfo.isTrio) {
      const hasFather = types.includes(KinshipType.PADRE);
      const hasMother = types.includes(KinshipType.MADRE);

      if (!hasFather || !hasMother) {
        return {
          valid: false,
          errorMessage:
            'Para exámenes TRIO debe agregar los datos del padre Y la madre'
        };
      }
    }

    if (examTypeInfo.isDuo) {
      const hasFather = types.includes(KinshipType.PADRE);
      const hasMother = types.includes(KinshipType.MADRE);

      if (!hasFather && !hasMother) {
        return {
          valid: false,
          errorMessage:
            'Para exámenes DUO debe agregar los datos del padre O la madre'
        };
      }
    }

    if (examTypeInfo.isFuturosPadres) {
      const hasFutureMother = types.includes(KinshipType.FUTURA_MADRE);
      const hasFutureFather = types.includes(KinshipType.FUTURO_PADRE);

      if (!hasFutureMother && !hasFutureFather) {
        return {
          valid: false,
          errorMessage:
            'Para exámenes de Futuros Padres debe agregar los datos de la pareja'
        };
      }
    }

    return { valid: true };
  }

  saveManualEntryWithFamily(
    payload: ManualEntryWithFamilyPayload
  ): Observable<ManualEntryWithFamilyResponse> {
    return this.httpClient
      .postRaw<ManualEntryWithFamilyResponse>(
        `${this.baseUrl}/manual-entry-with-family`,
        payload
      )
      .pipe(
        catchError((error: HttpErrorResponse) =>
          this.errorHandler.handleError('saveManualEntryWithFamily', error)
        )
      );
  }

  getKinshipTypeLabel(type: KinshipType): string {
    const labels: Record<KinshipType, string> = {
      [KinshipType.PADRE]: 'Padre',
      [KinshipType.MADRE]: 'Madre',
      [KinshipType.FUTURA_MADRE]: 'Futura Madre',
      [KinshipType.FUTURO_PADRE]: 'Futuro Padre'
    };
    return labels[type] || type;
  }

  clearCache(): void {
    this.examTypeCache.clear();
  }

  private getDefaultExamTypeInfo(): ExamTypeInfo {
    return {
      examGroupName: null,
      requiredRelatives: null,
      isTrio: false,
      isDuo: false,
      isFuturosPadres: false,
      requiresFamilyMembers: false
    };
  }

  private mapBackendResponse(response: ExamTypeBackendResponse): ExamTypeInfo {
    const familyType = response.familyType;

    return {
      examGroupName: response.examGroupName ?? null,
      requiredRelatives: response.requiredRelatives ?? null,
      isTrio: familyType === 'TRIO',
      isDuo: familyType === 'DUO',
      isFuturosPadres: familyType === 'FUTUROS_PADRES',
      requiresFamilyMembers: response.requiresFamilyMembers ?? false
    };
  }
}
